
import { inject } from '@angular/core';
import { Router, Routes, RouterStateSnapshot, ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { forkJoin, switchMap, map, of, catchError, throwError, tap, from } from 'rxjs';

import { ApiService, CREDS_KEY } from 'src/app/api.service';
import { StorageService } from 'src/app/storage.service';
import { IStateUserInfo } from './user/user.page';
import { DashboardPage } from './dashboard/dashboard.page';
import { ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';

// auxiliar per trobar paràmetres en el router-outlet general
// enlloc del router-outlet dels tabs.
const parentParam = (route: ActivatedRouteSnapshot, name: string) => {
  let currentRoute: ActivatedRouteSnapshot | null = route;
  while (currentRoute) {
    if (currentRoute.paramMap.has(name)) {
      return currentRoute.paramMap.get(name);
    }
    currentRoute = currentRoute.parent
  }
  return ''; // <- no s'ha trobat.
}

export const loggedInGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const storage = inject(StorageService);
  const router = inject(Router);
  const promise = storage.ready
    .then((rdy)=>storage.get(CREDS_KEY))
    .then(uid=>storage.get(`${CREDS_KEY}_${uid}`))
    .then((udata: any) => {
      return !!udata || router.parseUrl('/login')
    });
  return from(promise);
}

// obté l'estat de navegació (si hi és)
const navState =
  () => inject(Router).getCurrentNavigation()?.extras.state;

// ResolveFn que composa la info detall pel dashboard a base de composar crides a l'API
const dashBoardDetail = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  const router = inject(Router);
  const storage = inject(StorageService);
  const toastCtl = inject(ToastController);
  const translate = inject(TranslateService);
  const cid = parentParam(route, 'cid'); // Obtenim el `cid` de la ruta

  // potser rebem per paràmetre que s'ha signat un document 
  const signed_delivery = route.queryParams['doc'];
  const signed_event = route.queryParams['event'];
  
  // un observable auxiliar s'encarrega de reaccionar-hi
  const signature_post$ = (!signed_delivery||signed_event!='signing_complete')?
    of(null) :
    api.ipeDeliverySigned(signed_delivery)
    .pipe(
      tap(_=>toastCtl.create({
        message: translate.instant('dashboard.signature_ok'),
        buttons: [ translate.instant('revisions.entesos') ],
        duration: 4500,
        position: 'middle'
      }).then(toast=>toast.present()))
    );
  return forkJoin({
    userInfo: api.userInfo(),
    userCompanies: api.userCompanies(),
    languages: api.languages(),
    userLanguage: api.userLanguage(),
    isFAC: api.isFAC(cid!),
    upcoming_checkups:api.upcomingCheckups(cid!),
    signed_delivery: signature_post$
  }).pipe(
    switchMap((detail) => {
      const company_ids = Object.keys(detail.userCompanies);
      if(!!detail.signed_delivery) {
        // cal eliminar cachés dels EPIS i epistatus de l'usuari
        storage.setCache(`ipeWorkers_${cid!}_${detail.userInfo.contact_id}`, null);
        storage.setCache(`getIPEDeliveryStatus_${cid!}_${detail.userInfo.contact_id}`, null);
      }

      // Cas 1: No hi ha `cid`
      if (!cid) {
        api.logout().subscribe({
          error:()=>router.navigate(['/login']),
          complete:()=>router.navigate(['/login'])
        }); // Redirigir al login
        return of(null);
      }

      // Cas 2: El `cid` no és vàlid
      if (!company_ids.includes(cid)) {
        router.navigate(['/select-company']); // Redirigir a la selecció d'empresa
        return of(null);
      }

      // Cas 3: El `cid` és vàlid, fem la petició a `isPRL`
      const company = { id: cid, name: detail.userCompanies[cid] };
      return forkJoin({
        isprl:api.isPRL(cid).pipe(map(([{ isPRL }])=>isPRL)),
        epis: api.ipeWorkers(cid!, detail!.userInfo.contact_id)
      }).pipe(
        map(({isprl, epis})=>{
          return { ...detail, isPRL:isprl , company, epis };
        }),
        catchError((error) => {
          console.error('Error al fer la petició a isPRL:', error);
          // Redirigir al login en cas d'error
          api.logout().subscribe({
            error:()=>router.navigate(['/login']),
            complete:()=>router.navigate(['/login'])
          }); 
          return of(null);
        })
      );
    }),
    catchError((error) => {
      console.error('Error al carregar dades inicials:', error);
      return of(null);
    })
  );
};

export const selectCompanyDetail = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  return forkJoin({
    userInfo: api.userInfo(),
    userCompanies: api.userCompanies(),
  }).pipe(
    map((data) => ({
      userInfo: data.userInfo,
      userCompanies: Object.entries(data.userCompanies).map(([id, name]) => ({
        id,
        name,
      })),
    }))
  );
};

// Si l'usuari té només una empresa, redirigeix al dashboard d'aquesta empresa.
const selectCompanyGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const api = inject(ApiService);
  console.log('xxxxselectCompanyGuard called');

  const currentCid = route.queryParamMap.get('cid');
  if (currentCid) {
    return true;
  }

  return selectCompanyDetail(route, state).pipe(
    map(data => {
      const companies = data.userCompanies;
      console.log('xxxCompanies:', companies);
      if (companies.length === 1) {
        // return router.parseUrl(`/select-company/${companies[0].id}`);
        return router.parseUrl(`/select-company?cid=${companies[0].id}`); // Envia el id de la empresa
      } else if (companies.length > 1) {
        return true;
      }

      console.warn('No companies found, redirecting to login');
      return api.logout().pipe(
        catchError((err)=>of(router.parseUrl('/login'))),
        switchMap(()=>of(router.parseUrl('/login')))
      )
    }),
    catchError((error) => {
      console.error('Error fetching company details:', error);
      return api.logout().pipe(
        catchError((err)=>of(router.parseUrl('/login'))),
        switchMap(()=>of(router.parseUrl('/login')))
      )
    })
  );
};

const authToken = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot)=>{
  const code = route.queryParamMap.get('code');
  const api = inject(ApiService);
  const router = inject(Router);
  if (!code) {
    throwError(()=>new Error('Not authorized')).pipe(api.uify()).subscribe();
    return router.parseUrl('/login');
  }
  return api.remataLoginGoogle({ code }).pipe(map(auth=>!!auth))
}

export const companyDetailResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  const cid = parentParam(route, 'cid');
  if (!cid) {
    return of(null);
  }

  return api.userCompanies().pipe(
    map((companies) => {
      const company = companies[cid];
      if (!company) {
        return null;
      }
      return { id: cid, name: company };
    })
  );
};

export const workplacesResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const company_id = parentParam(route, 'cid');
  const api = inject(ApiService);
  return api.workPlaces(company_id!).pipe( api.uify());
}

export const idTypesResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  return api.identifierTypes().pipe(api.uify());
}
export const sexIdsResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  return api.sexIds().pipe( api.uify());
}
export const languagesResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  return api.languages().pipe( api.uify());
}

export const jobsResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const company_id = parentParam(route, 'cid');
  const api = inject(ApiService);
  return api.companyJobs(company_id!).pipe( api.uify());
}

export const userInfoResolver = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const api = inject(ApiService);
  return api.userInfo().pipe(api.uify());
}

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./login/login.page').then(m => m.LoginPage),
    resolve:{
      canCreateAccount:(route: ActivatedRouteSnapshot, state: RouterStateSnapshot)=>{
        return inject(ApiService).accountCreation();
      }
    }
  },
  {
    path: 'select-company',
    loadComponent: () => import('./select-company/select-company.page').then(m => m.SelectCompanyPage),
    resolve: { detail: selectCompanyDetail },
    canActivate: [selectCompanyGuard]
  },
  {
    path: 'select-company/:cid',
    loadComponent: () => import('./select-company/select-company.page').then(m => m.SelectCompanyPage),
    resolve: { detail: selectCompanyDetail },
    canActivate: [selectCompanyGuard]
  },
  {
    path: ':cid/dashboard',
    component: DashboardPage,
    canActivate: [loggedInGuard],
    resolve: { detail: dashBoardDetail }
  }, {
    path: ':cid/dashboard/user',
    loadChildren: () => import('./user/user.routes').then(m => m.userRoutes),
  },
  {
    path: ':cid/dashboard/notis',
    loadComponent: () => import('./notis/notis.page').then(m => m.NotisPage)
  },
  {
    path: ':cid/dashboard/notis/noti-detall/:nid',
    loadComponent: () => import('./notis/noti-detall/noti-detall.page').then(m => m.NotiDetallPage)
  },
  {
    path: ':cid/tabs',
    loadChildren: () => import('./tabs/tabs.routes').then((m) => m.routes),
  },
  {
    path: 'callback',
    loadComponent: () => import('./select-company/select-company.page').then(m => m.SelectCompanyPage),
    resolve: { detail: selectCompanyDetail },
    canActivate: [authToken]
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
];
